import ErrorBoundary from "@/shared/components/error-boundary";
import { Spinner, Tab, Tabs } from "@nextui-org/react";
import Company from "./Company/Company";
import Overview from "./Overview/Overview";
import PotentialIssues from "./PotentialIssues/PotentialIssues";
import SupplyChain from "./SupplyChain/SupplyChain";

import { AgentSessionStatus, AgentSessionStepType, MultiVendorAgentSession } from '@/modules/sessions/types';
import VendorLogo from "@/shared/components/VendorSearchAutocomplete/VendorLogo";
import useEffectAsync from '@/shared/hooks/useEffectAsync';
import parsePromise from '@/shared/utils/parsePromise';
import clsx from 'clsx';
import { Loader } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getAgentData, getAgentStateActions, useAgentSessionData, useAgentStepData } from '../../states';
import { VendorRiskSectionEnum } from '../../types/vendorAssessment';
import { VendorRiskResponseItemForVendorOverview } from '../../types/vendorOverviewSection';
import ReviewResponses from '../review-responses';
import { VendorRiskLevel } from './Overview/OverviewCards.types';
import RiskLevelChip from './Overview/RiskLevelChip';
import ReassessUpdateCard from "./ReassessUpdateCard";
import { useVendorRiskAssessment } from './useVendorRiskAssessment/useVendorRiskAssessment';
import { useVendorRiskAssessmentStore, VendorPageType } from './useVendorRiskAssessment/useVendorRiskAssessmentStore';
// Socket hooks - centralized at parent level to prevent duplicate connections across tabs
import { getZaniaSocket } from "@/infra/sockets";
import { useControlEventsSubscription } from "@/modules/agent/hooks/useControlEventsSubscription";
import { useControlStatus } from "@/modules/agent/hooks/useControlStatus";
import { useSessionSubscription } from "@/modules/agent/hooks/useSessionSubscription";
import { AgentSocketEvents } from "../../types/events";

interface Params {
  id: string;
}

const VendorRiskAssessment = () => {
  const { id } = useParams<keyof Params>() as Params;
  const { isSaving } = useVendorRiskAssessmentStore();
  const {
    combinedVendorRiskData,
    selectedTab,
    handleTabChange,
    loadVendorRiskData,
    vendorOverview,
  } = useVendorRiskAssessment();

  const { reset } = useVendorRiskAssessmentStore();

  // Add state to track the risk level for live updates
  const [liveRiskLevel, setLiveRiskLevel] = useState<VendorRiskLevel | null>(null);
  const changesMadeRef = useRef(0);

  useEffectAsync(async () => {
    await parsePromise(loadVendorRiskData());
  }, [loadVendorRiskData]);

  // Monitor vendor overview for changes to risk_rating
  useEffect(() => {
    const riskRatingItem = vendorOverview.find(item => item.key === "risk_rating") as
      VendorRiskResponseItemForVendorOverview<"risk_rating"> | undefined;

    if (riskRatingItem?.value?.value) {
      // Verify risk level is a valid VendorRiskLevel value
      const riskValue = riskRatingItem.value.value;
      if (riskValue === "High" || riskValue === "Medium" || riskValue === "Low") {
        setLiveRiskLevel(riskValue);
      }
    }
  }, [vendorOverview]);

  // Subscribe to store changes to detect remote updates
  useEffect(() => {
    const unsubscribe = useVendorRiskAssessmentStore.subscribe((state) => {
      // Only update if the changesMade value has increased (indicating a store update)
      if (state.changesMade !== changesMadeRef.current) {
        changesMadeRef.current = state.changesMade;

        // Optimize: only check risk rating if vendorOverview actually changed
        if (state.vendorOverview.length > 0) {
          const riskRatingItem = state.vendorOverview.find(item => item.key === "risk_rating") as
            VendorRiskResponseItemForVendorOverview<"risk_rating"> | undefined;

          if (riskRatingItem?.value?.value) {
            const riskValue = riskRatingItem.value.value;
            if (riskValue === "High" || riskValue === "Medium" || riskValue === "Low") {
              setLiveRiskLevel(riskValue);
            }
          }
        }
      }
    });

    return unsubscribe;
  }, []);

  const [reassessUpdateCardVisible, setReassessUpdateCardVisible] = useState(true);

  const sessionData = useAgentSessionData(id);

  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  // Extract risk level calculation into a memoized value that will update from either 
  // combinedVendorRiskData or liveRiskLevel changes
  const currentRiskLevel = useMemo(() => {
    // If we have a live risk level from the direct store updates, use that
    if (liveRiskLevel) {
      return liveRiskLevel;
    }

    // Otherwise, fall back to the combinedVendorRiskData method
    if (!combinedVendorRiskData) return null;

    const riskRatingItem = combinedVendorRiskData[VendorRiskSectionEnum.vendor_overview]?.find(
      (item: any) => item.key === "risk_rating"
    ) as VendorRiskResponseItemForVendorOverview<"risk_rating"> | undefined;

    const riskValue = riskRatingItem?.value.value;
    return (riskValue === "High" || riskValue === "Medium" || riskValue === "Low") ? riskValue : null;
  }, [combinedVendorRiskData, liveRiskLevel]);

  const stepData = useAgentStepData(id);
  const isReportReady =
    stepData?.find((step) => step.type === AgentSessionStepType.PREPARE_REPORT)
      ?.status === AgentSessionStatus.COMPLETE;
  const criticality = vendorOverview.find(
        (overviewItem) => overviewItem.key === "business_criticality"
      );

  // Initialize socket connections at parent level to prevent duplicate connections
  // when switching between tabs (Overview, Company, Supply Chain, etc.)
  // These hooks manage session-level socket events and should only be initialized once
  useControlStatus(id);
  useSessionSubscription(id);
  useControlEventsSubscription(id);

  // Listen for socket update events to reset saving state AND handle live updates
  useEffect(() => {
    const socket = getZaniaSocket();
    
    const handleControlUpdated = (data: any) => {
      // Check if the update is for this session
      if (data.agent_session_id !== id) return;
      
      const store = useVendorRiskAssessmentStore.getState();
      
      // Always reset saving state when we get confirmation from server
      store.setIsSaving(false);
      
      // Handle live updates for vendor assessment controls
      if (data.control?.section) {
        const { control } = data;
        const controlSection = control.section as VendorRiskSectionEnum;
        
        // ============ PREVENT CURSOR JUMPS & TEXT LOSS ============
        // Get the currently selected/active control ID
        const agentData = getAgentData(id);
        const selectedId = agentData?.mainData?.selectedId;
        
        // CHECK 1: Is user actively typing in this control's input field?
        const activeElement = document.activeElement;
        const isInputFocused = activeElement && 
          (activeElement.id?.includes(control.key) || 
           activeElement.getAttribute('aria-label')?.includes(control.key));
        
        // CHECK 2: Is this update very recent (within last 2 seconds)?
        const lastUpdate = agentData?.mainData?.lastUpdate;
        const isRecentLocalUpdate = lastUpdate && 
          !lastUpdate.isRemoteUpdate && 
          (Date.now() - lastUpdate.timestamp) < 2000;
        
        // SKIP update if:
        // - Input field is currently focused (prevents cursor jumps during typing), OR
        // - User just made a local edit (prevents self-echo)
        if (isInputFocused || (selectedId === control.key && isRecentLocalUpdate)) {
          console.log('[SOCKET] Skipping update - user actively editing:', control.key, 
            'focused:', !!isInputFocused, 'recent:', !!isRecentLocalUpdate);
          return;
        }
        
        // Mark this as a remote update for tracking
        if (agentData) {
          const { setAgentData } = getAgentStateActions();
          setAgentData(id, {
            ...agentData,
            mainData: {
              ...agentData.mainData,
              lastUpdate: {
                timestamp: Date.now(),
                isRemoteUpdate: true
              }
            }
          });
        }
        // =========================================================
        
        switch (controlSection) {
          case VendorRiskSectionEnum.vendor_overview: {
            const updatedOverview = [...store.vendorOverview];
            const overviewIndex = updatedOverview.findIndex((item) => item.key === control.key);
            
            if (overviewIndex !== -1) {
              if (control.key === "data_types_handled") {
                updatedOverview[overviewIndex] = {
                  ...updatedOverview[overviewIndex],
                  value: control.value,
                };
              } else {
                updatedOverview[overviewIndex] = {
                  ...updatedOverview[overviewIndex],
                  ...control,
                };
              }
              store.setVendorOverview(updatedOverview, true); // true = don't increment changesMade
            }
            break;
          }
          
          case VendorRiskSectionEnum.company_profile: {
            const updatedCompanyProfile = [...store.vendorCompanyData];
            const companyProfileIndex = updatedCompanyProfile.findIndex(
              (item) => item.key === control.key
            );
            
            if (companyProfileIndex !== -1) {
              updatedCompanyProfile[companyProfileIndex] = {
                ...updatedCompanyProfile[companyProfileIndex],
                ...control,
                edited: true,
              };
              store.setVendorCompanyData(updatedCompanyProfile, true);
            }
            break;
          }
          
          case VendorRiskSectionEnum.vendor_supply_chain: {
            const updatedSupplyChain = [...store.vendorSupplyChainData];
            const supplyChainIndex = updatedSupplyChain.findIndex(
              (item) => item.key === control.key
            );
            
            if (supplyChainIndex !== -1) {
              updatedSupplyChain[supplyChainIndex] = {
                ...updatedSupplyChain[supplyChainIndex],
                ...control,
                edited: true,
              };
              store.setVendorSupplyChain(updatedSupplyChain, true);
            }
            break;
          }
          
          case VendorRiskSectionEnum.potential_issues: {
            const updatedPotentialIssues = [...store.vendorPotentialIssuesData];
            const potentialIssuesIndex = updatedPotentialIssues.findIndex(
              (item) => item.key === control.key
            );
            
            if (potentialIssuesIndex !== -1) {
              updatedPotentialIssues[potentialIssuesIndex] = {
                ...updatedPotentialIssues[potentialIssuesIndex],
                ...control,
                edited: true,
              };
              store.setVendorPotentialIssues(updatedPotentialIssues, true);
            }
            break;
          }
        }
      }
    };

    socket.on(AgentSocketEvents.CONTROL_UPDATED, handleControlUpdated);

    return () => {
      socket.off(AgentSocketEvents.CONTROL_UPDATED, handleControlUpdated);
    };
  }, [id]);

  return combinedVendorRiskData ? (
    <ErrorBoundary fallback={null}>
      <div className="h-full w-full rounded-lg flex flex-col">
        {/* header */}
        <div className="bg-white sticky top-0 z-50 w-full">
          <div className="px-6 pt-4 pb-3 flex flex-row gap-3 w-full items-center bg-white">
            <div className="text-lg font-[550] flex items-center gap-[6px]">
              {(sessionData as MultiVendorAgentSession)?.multi_file_vendor_assessment?.vendor_logo_url && <VendorLogo
                alt={(sessionData as MultiVendorAgentSession)?.multi_file_vendor_assessment?.vendor_name}
                src={(sessionData as MultiVendorAgentSession)?.multi_file_vendor_assessment?.vendor_logo_url ?? ""}
                className="size-8 rounded-full"
                radius="sm"
                needsAuthToken
              />}

             <span>
               {(sessionData as MultiVendorAgentSession)
                .multi_file_vendor_assessment?.vendor_name ?? "Vendor Name"}
                {` `} {(sessionData as MultiVendorAgentSession)
                  .multi_file_vendor_assessment?.product_name ?? "Product Name"}
             </span>
            </div>
            {currentRiskLevel && <RiskLevelChip riskLevel={currentRiskLevel} />}
            <div className="flex items-center h-7 gap-1">
              <Loader
                className={`w-4 h-4 animate-spin ${isSaving ? '' : 'invisible'}`}
              />
              {isSaving && (
                <p className="text-tiny text-[#A5A5A5] text-[10px]">Saving</p>
              )}
            </div>
          </div>

          <Tabs
            variant="underlined"
            selectedKey={selectedTab}
            classNames={{
              tab: "rounded-none px-4 text-[12px] text-[#18181B]",
              base: "rounded-none",
              cursor: "rounded-none",
              panel: "rounded-none",
              tabContent: "rounded-none",
              tabList: "rounded-none p-0",
              wrapper: "rounded-none",
            }}
            className="border-b-1 bg-white w-full"
            onSelectionChange={(key) => handleTabChange(key as VendorPageType)}
          >
            <Tab
              className={clsx("rounded-none", {
                'after:content-[""] after:absolute after:bottom-0 after:left-0 after:w-full after:h-full after:bg-gradient-to-t  after:from-[#E7F2FF] after:to-[#FFFFFF] after:border-b-2 after:border-b-[#006FEE]':
                  selectedTab === "OVERVIEW",
              })}
              key="OVERVIEW"
              title="Overview"
            />
            <Tab
              className={clsx("rounded-none", {
                'after:content-[""] after:absolute after:bottom-0 after:left-0 after:w-full after:h-full after:bg-gradient-to-t  after:from-[#E7F2FF] after:to-[#FFFFFF] after:border-b-2 after:border-b-[#006FEE]':
                  selectedTab === "COMPANY",
              })}
              key="COMPANY"
              title="Company"
            />
            <Tab
              className={clsx("rounded-none", {
                'after:content-[""] after:absolute after:bottom-0 after:left-0 after:w-full after:h-full after:bg-gradient-to-t after:from-[#E7F2FF] after:to-[#FFFFFF] after:border-b-2 after:border-b-[#006FEE]':
                  selectedTab === "SUPPLY_CHAIN",
              })}
              key="SUPPLY_CHAIN"
              title="Supply Chain"
            />
            <Tab
              className={clsx("rounded-none", {
                'after:content-[""] after:absolute after:bottom-0 after:left-0 after:w-full after:h-full after:bg-gradient-to-t after:from-[#E7F2FF] after:to-[#FFFFFF] after:border-b-2 after:border-b-[#006FEE]':
                  selectedTab === "DETAILED_REVIEW",
              })}
              key="DETAILED_REVIEW"
              title="Detailed Review"
            />
            <Tab
              className={clsx("rounded-none", {
                'after:content-[""] after:absolute after:bottom-0 after:left-0 after:w-full after:h-full after:bg-gradient-to-t after:from-[#E7F2FF] after:to-[#FFFFFF] after:border-b-2 after:border-b-[#006FEE]':
                  selectedTab === "POTENTIAL_ISSUES",
              })}
              key="POTENTIAL_ISSUES"
              title="Issues"
            />
          </Tabs>
        </div>
        <div className="h-full overflow-x-auto bg-white">
          {selectedTab === "OVERVIEW" && <Overview />}
          {selectedTab === "COMPANY" && <Company />}
          {selectedTab === "SUPPLY_CHAIN" && <SupplyChain />}
          {selectedTab === "DETAILED_REVIEW" && (
            <ReviewResponses
              hideAgentTypeFromHeader={true}
              disableRoundedBorder={true}
            />
          )}
          {selectedTab === "POTENTIAL_ISSUES" && <PotentialIssues />}
        </div>
      </div>
    </ErrorBoundary>
  ) : (
    <div className="w-full h-full bg-white flex items-center justify-center px-24">
      <Spinner />
    </div>
  );
};

export default VendorRiskAssessment;
